<template>
  <div class="px-8">
    <!-- this list shows when new items are added -->
    <div
      v-if="resources.length || resourcesToBeSaved.length"
      class="border-none shared-table-container"
    >
      <div class="shared-table-innermost-container" :style="{ maxHeight: '8.5rem' }">
        <table class="shared-table">
          <thead>
            <tr class="bg-transparent border-none shared-table-headings">
              <th
                v-for="(header, index) in $options.tableHeaders"
                :key="`heading-${index}`"
                class="shared-table-headings-heading"
              >
                <div class="flex items-center justify-center">
                  {{ header }}
                </div>
              </th>
              <th class="shared-table-headings-heading"></th>
            </tr>
          </thead>
          <tbody>
            <!-- resources existing in database -->
            <tr
              v-for="(r, index) in resources"
              :key="`resource-${index}`"
              class="shared-table-cells"
            >
              <td class="shared-table-cells-cell">
                <span class="inline-block w-16 py-1 text-white bg-green-500 rounded">saved</span>
              </td>
              <td class="shared-table-cells-cell">
                <div class="w-40 truncate">{{ r.title }}</div>
              </td>
              <td class="truncate whitespace-normal shared-table-cells-cell">
                <div class="w-40 truncate">
                  <span v-if="r.link">{{ r.link }}</span>
                  <a
                    v-else
                    :href="r.file_path"
                    target="_blank"
                    :download="r.title"
                    class="underline"
                  >
                    pdf file
                  </a>
                </div>
              </td>
              <td class="shared-table-cells-cell">
                <div class="flex justify-center">
                  <button
                    class="mr-2 shared-table-cells-button round edit"
                    @click="editResource('saved', r)"
                  >
                    <VSvgIcon name="pencil" class="h-5" />
                  </button>
                  <button
                    class="shared-table-cells-button round delete"
                    @click="handleDeleteResource(r.id)"
                  >
                    <VSvgIcon name="trash" class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
            <!-- resources yet to be saved in database -->
            <tr
              v-for="(r, index) in resourcesToBeSaved"
              :key="`resource-to-be-saved-${index}`"
              class="shared-table-cells"
            >
              <td class="shared-table-cells-cell">
                <span class="inline-block w-16 py-1 text-white rounded bg-ricoma-gray-400">
                  draft
                </span>
              </td>
              <td class="shared-table-cells-cell">
                {{ r.title }}
              </td>
              <td class="shared-table-cells-cell">
                {{ r.file ? r.file.name : r.link }}
              </td>
              <td class="shared-table-cells-cell">
                <div class="flex justify-center">
                  <button
                    class="mr-2 shared-table-cells-button round edit"
                    @click="editResource('draft', r)"
                  >
                    <VSvgIcon name="pencil" class="h-5" />
                  </button>
                  <button
                    class="shared-table-cells-button round delete"
                    @click="handleRemoveResource(r.title)"
                  >
                    <VSvgIcon name="trash" class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <form
      @submit.prevent=""
      :class="[isEditable && 'p-4 mb-4 border-2 border-ricoma-orange-300 rounded shadow-lg']"
    >
      <div class="w-1/2 pr-4">
        <VFormInput
          placeholder="Enter title"
          label="Title*"
          :error="errors.title"
          :reset="resetField"
          :initialValue="resourceEditSaved.title || resourceEditDraft.title"
          @update="value => handleSetField('title', value)"
        />
      </div>
      <div class="flex items-center mb-6">
        <div class="h-12 mb-2 -mt-6">
          <VFormUpload
            label="File*"
            :reset="resetField"
            :accept="['application/pdf', 'image/*', '.dst']"
            :initialValue="resourceEditSaved.file || resourceEditDraft.file"
            @upload="value => handleSetField('file', value[0])"
          />
        </div>
        <div class="px-12">or</div>
        <div class="w-4/6">
          <VFormInput
            placeholder="Enter Link"
            label="Link*"
            :error="errors.link"
            :reset="resetField"
            :initialValue="resourceEditSaved.link || resourceEditDraft.link"
            @update="v => handleSetField('link', v)"
          />
        </div>
      </div>
      <div>
        <button
          v-if="!isEditable"
          type="submit"
          class="flex items-center justify-center w-full p-4 text-sm font-semibold text-white uppercase bg-gray-900 rounded"
          @click="handleAddResource"
        >
          <VSvgIcon name="plus" class="mr-2" /> Add resource
        </button>
        <button
          v-else
          type="submit"
          class="flex items-center justify-center w-full p-4 text-sm font-semibold text-white uppercase bg-gray-900 rounded"
          @click="handleEditResouce"
        >
          <VSvgIcon name="plus" class="mr-2" /> Edit resource
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import VFormInput from '@/components/base/VFormInput';
import VFormUpload from '@/components/base/VFormUpload';
import VSvgIcon from '@/components/base/VSvgIcon';

import { formMixin } from '@/mixins';

export default {
  name: 'PreTrainingFormResources',
  components: { VFormInput, VFormUpload, VSvgIcon },
  props: {
    language: {
      type: Object,
      required: false,
      default: () => ({ code: 'en' }),
    },
  },
  mixins: [formMixin({ title: '', link: '', file: null }, ['file', 'link'])],
  data() {
    return {
      resetField: false,
      resources: [],
      resourcesToBeSaved: [],
      resourceEditSaved: {},
      resourceEditDraft: {},
    };
  },
  watch: {
    resourcesToBeSaved(resources) {
      this.$emit('update', resources);
    },
    async language() {
      this.resources = await this.fetchResources(
        this.$http,
        this.$route.params.id,
        this.language.code,
      );
    },
  },
  computed: {
    editableDraft() {
      return !!Object.keys(this.resourceEditDraft).length;
    },
    editableSaved() {
      return !!Object.keys(this.resourceEditSaved).length;
    },
    isEditable() {
      return this.editableDraft || this.editableSaved;
    },
  },
  methods: {
    editResource(type, resource) {
      type === 'draft'
        ? ((this.resourceEditDraft = resource), (this.resourceEditSaved = {}))
        : ((this.resourceEditSaved = resource), (this.resourceEditDraft = {}));
    },
    filterResources(propName, propValue) {
      return data => data[propName] !== propValue;
    },
    async handleAddResource() {
      const submitted = await this.handleSubmit(({ title, link, file }) => {
        const { code: lang_code } = this.language;
        this.resourcesToBeSaved = this.resourcesToBeSaved.concat({ title, link, file, lang_code });
        this.resetField = true;
      })();

      if (submitted) this.resetField = false;
    },
    async handleEditResouce() {
      const submitted = await this.handleSubmit(async ({ title, link, file }) => {
        if (this.editableDraft) {
          this.resourcesToBeSaved = this.resourcesToBeSaved.map(resource =>
            resource.title === this.resourceEditDraft.title
              ? { ...resource, title, link, file }
              : resource,
          );
        } else {
          const formData = new FormData();
          // append each value to form data
          Object.entries(this.resourceEditSaved).forEach(([key, value]) =>
            formData.append(key, value),
          );
          // overide following fields with latest chages
          if (title) formData.set('title', title);
          if (link) formData.set('link', link);
          if (file) formData.set('file', file);

          // append lang_code
          formData.append('lang_code', this.language.code);

          const videoId = this.$route.params.id;
          const res = await this.$http.post(
            `/admin/pre-training-videos/${videoId}/resources/${this.resourceEditSaved.id}`,
            formData,
          );
          const resource = res.data.data;

          // update resources array with new changes
          this.resources = this.resources.map(r => (r.id === resource.id ? resource : r));
        }

        // reset fields
        this.resetField = true;
        this.resourceEditDraft = {};
        this.resourceEditSaved = {};
      })();

      if (submitted) this.resetField = false;
    },
    handleRemoveResource(title) {
      this.resourcesToBeSaved = this.resourcesToBeSaved.filter(
        this.filterResources('title', title),
      );
    },
    async handleDeleteResource(id) {
      const videoId = this.$route.params.id;
      await this.$http.delete(`/admin/pre-training-videos/${videoId}/resources/${id}`);
      this.resources = this.resources.filter(this.filterResources('id', id));
    },
    async fetchResources(http, videoId, lang_code) {
      const { data } = await this.$http.get(`/admin/pre-training/${videoId}/resources`, {
        params: { lang_code },
      });
      return data.data;
    },
  },
  async mounted() {
    this.resources = await this.fetchResources(
      this.$http,
      this.$route.params.id,
      this.language.code,
    );
  },
  created() {
    this.$options.tableHeaders = ['state', 'title', 'file/link name', 'actions'];
  },
  tableHeaders: [],
};
</script>
